import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

const STORAGE_KEY = 'glubesTheme';
const PRIMARY_LIGHT_KEY = 'glubesThemePrimaryMainLight';
const PRIMARY_DARK_KEY = 'glubesThemePrimaryMainDark';
/** @deprecated Migrated to light/dark keys on read */
const PRIMARY_LEGACY_KEY = 'glubesThemePrimaryMain';

export type ThemePreference = 'light' | 'dark' | 'system';

const HEX6 = /^#[0-9A-Fa-f]{6}$/;

function readStoredPrimaryMains(): { light: string | null; dark: string | null } {
  try {
    let light: string | null = null;
    let dark: string | null = null;
    const l = localStorage.getItem(PRIMARY_LIGHT_KEY);
    const d = localStorage.getItem(PRIMARY_DARK_KEY);
    if (l && HEX6.test(l)) light = l;
    if (d && HEX6.test(d)) dark = d;

    const legacy = localStorage.getItem(PRIMARY_LEGACY_KEY);
    if (legacy && HEX6.test(legacy)) {
      if (light == null) {
        light = legacy;
        localStorage.setItem(PRIMARY_LIGHT_KEY, legacy);
      }
      if (dark == null) {
        dark = legacy;
        localStorage.setItem(PRIMARY_DARK_KEY, legacy);
      }
      localStorage.removeItem(PRIMARY_LEGACY_KEY);
    }
    return { light, dark };
  } catch {
    return { light: null, dark: null };
  }
}

function readStoredPreference(): ThemePreference {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    if (v === 'light' || v === 'dark' || v === 'system') {
      return v;
    }
  } catch {
    /* ignore */
  }
  return 'system';
}

function resolveTheme(preference: ThemePreference, systemDark: boolean): 'light' | 'dark' {
  if (preference === 'light') {
    return 'light';
  }
  if (preference === 'dark') {
    return 'dark';
  }
  return systemDark ? 'dark' : 'light';
}

interface ThemeContextValue {
  preference: ThemePreference;
  /** Effective appearance after applying system preference */
  resolvedTheme: 'light' | 'dark';
  setPreference: (value: ThemePreference) => void;
  /** Switch light ↔ dark. Shift+click restores system (follow OS). */
  toggleTheme: (event?: { shiftKey?: boolean }) => void;
  /** Custom primary for light appearance (`#rrggbb`), or `null` for brand default. */
  primaryMainLight: string | null;
  /** Custom primary for dark appearance (`#rrggbb`), or `null` for brand default. */
  primaryMainDark: string | null;
  setPrimaryMainLight: (hex: string | null) => void;
  setPrimaryMainDark: (hex: string | null) => void;
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

export function ThemeProvider({ children }: { children: React.ReactNode }) {
  const [preference, setPreferenceState] = useState<ThemePreference>(() => readStoredPreference());
  const [primaryMains, setPrimaryMains] = useState(() => readStoredPrimaryMains());

  const setPreference = useCallback((value: ThemePreference) => {
    setPreferenceState(value);
    try {
      localStorage.setItem(STORAGE_KEY, value);
    } catch {
      /* ignore */
    }
  }, []);

  const setPrimaryMainLight = useCallback((hex: string | null) => {
    if (hex != null && !HEX6.test(hex)) {
      return;
    }
    setPrimaryMains((prev) => ({ ...prev, light: hex }));
    try {
      if (hex == null) {
        localStorage.removeItem(PRIMARY_LIGHT_KEY);
      } else {
        localStorage.setItem(PRIMARY_LIGHT_KEY, hex);
      }
    } catch {
      /* ignore */
    }
  }, []);

  const setPrimaryMainDark = useCallback((hex: string | null) => {
    if (hex != null && !HEX6.test(hex)) {
      return;
    }
    setPrimaryMains((prev) => ({ ...prev, dark: hex }));
    try {
      if (hex == null) {
        localStorage.removeItem(PRIMARY_DARK_KEY);
      } else {
        localStorage.setItem(PRIMARY_DARK_KEY, hex);
      }
    } catch {
      /* ignore */
    }
  }, []);

  const [systemDark, setSystemDark] = useState(() =>
    typeof window !== 'undefined' ? window.matchMedia('(prefers-color-scheme: dark)').matches : false
  );

  useEffect(() => {
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const onChange = () => setSystemDark(mq.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  const resolvedTheme = useMemo(
    () => resolveTheme(preference, systemDark),
    [preference, systemDark]
  );

  useEffect(() => {
    document.body.classList.toggle('dark', resolvedTheme === 'dark');
  }, [resolvedTheme]);

  const toggleTheme = useCallback(
    (event?: { shiftKey?: boolean }) => {
      if (event?.shiftKey) {
        setPreference('system');
        return;
      }
      const next =
        resolveTheme(preference, window.matchMedia('(prefers-color-scheme: dark)').matches) === 'dark'
          ? 'light'
          : 'dark';
      setPreference(next);
    },
    [preference, setPreference]
  );

  const value = useMemo(
    () => ({
      preference,
      resolvedTheme,
      setPreference,
      toggleTheme,
      primaryMainLight: primaryMains.light,
      primaryMainDark: primaryMains.dark,
      setPrimaryMainLight,
      setPrimaryMainDark,
    }),
    [
      preference,
      resolvedTheme,
      setPreference,
      toggleTheme,
      primaryMains.light,
      primaryMains.dark,
      setPrimaryMainLight,
      setPrimaryMainDark,
    ]
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme(): ThemeContextValue {
  const ctx = useContext(ThemeContext);
  if (ctx == null) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return ctx;
}
